import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import ImportCsvExcel from '../components/ImportCsv';
import { useSelector } from 'react-redux';
import AdminCreateUser from '../components/AdminCreateUser';
import Filters from '../components/Filters';

/**
 * @file
 * @module Pages/AdminUsers
 * @description AdminUsers est une page qui une liste de tous les utilisateurs de l'application.
 * - Elle permet à un administrateur de voir tous les utilisateurs, de les filtrer et de les exporter en CSV.
 * - Elle permet également à l'administrateur de vérifier et de mettre à jour les utilisateurs.
 * - Elle permet également à l'administrateur de supprimer des utilisateurs sélectionnés.
 * - Cette page utilise le hook useState de React pour gérer l'état des utilisateurs, de la recherche, des colonnes et des utilisateurs sélectionnés.
 * - Cette page utilise le hook useEffect de React pour appeler l'API lors du chargement de la page.
 * - Cette page utilise le hook useSelector de 'react-redux' pour accéder au token de l'utilisateur dans le store Redux.
 * - Cette page utilise la bibliothèque PapaParse pour exporter les utilisateurs en CSV.
 * - Cette page utilise la méthode fetch de JavaScript pour appeler l'API.
 * - Cette page utilise la méthode filter de JavaScript pour filtrer les utilisateurs.
 */

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const UserCount = ({ count }) => (
  <div className="alert alert-info mb-6">
    <i className="fas fa-users mr-2"></i>
    <strong>Nombre d'utilisateurs affichés : {count}</strong>
  </div>
);

const AdminUsers = () => {
  const token = useSelector((state) => state.user.token);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // État pour les utilisateurs sélectionnés
  const [currentPage, setCurrentPage] = useState(1); // Page actuelle
  const [usersPerPage] = useState(50); // Nombre d'utilisateurs par page
  const [filters, setFilters] = useState([]);
  const [startFromId, setStartFromId] = useState('');
const [reverseOrder, setReverseOrder] = useState(false);

  const columns = [
    { value: 'all', label: 'Tous les champs' },
    { value: 'id', label: 'ID' },
    { value: 'username', label: 'Username' },
    { value: 'firstname', label: 'Prénom' },
    { value: 'lastname', label: 'Nom' },
    { value: 'active', label: 'Actif' },
    { value: 'is_admin', label: 'Admin' },
    { value: 'siret', label: 'Siret' },
    { value: 'chefEntreprise', label: 'Chef Entreprise'},
    { value: 'company', label: 'Company' },
    { value: 'creationDate', label: 'Date de création' },
    { value: 'lastactive', label: 'Dernière activité' },
    { value: 'loginCount', label: 'Connexions' },
  ];
  

  // Fonction pour sélectionner tous les utilisateurs affichés
  const handleSelectAllUsers = () => {
    const currentUsersIds = currentUsers.map(user => user.id);
    setSelectedUsers((prevSelectedUsers) =>
      prevSelectedUsers.length === currentUsersIds.length ? [] : currentUsersIds
    );
  };


  // Fonction pour sélectionner/désélectionner les utilisateurs
  const handleSelectUser = (id) => {
    setSelectedUsers((prevSelectedUsers) =>
      prevSelectedUsers.includes(id)
        ? prevSelectedUsers.filter(userId => userId !== id)
        : [...prevSelectedUsers, id]
    );
  };

  const checkAndUpdateUsers = async (users) => {
    console.log("Début de la vérification et de la mise à jour des utilisateurs");
    console.log("Utilisateurs à vérifier :", users);
    
    for (let user of users) {
      console.log(`Vérification de l'utilisateur avec ID ${user.id} et SIRET ${user.siret}`);
      try {
        const response = await fetch(`https://api.hapluspme.com/v1/3EA50081-D5B9-4DD3-8136-0A94B5DFF39F/adherents/${user.siret}`);
        const data = await response.json();
  
        console.log(`Statut de l'utilisateur ${user.id} reçu :`, data.status);
  
        if (data.status === 2) {
          console.log(`Mise à jour de l'utilisateur ${user.id} en tant qu'inactif`);
          
          const updateResponse = await fetch(apiPrefix + `admin/update/${user.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...user,
              active: false,
            }),
          });
  
          if (!updateResponse.ok) {
            console.error(`Erreur lors de la mise à jour de l'utilisateur ${user.id}`);
            throw new Error(`Erreur lors de la mise à jour de l'utilisateur ${user.id}`);
          } else {
            console.log(`Utilisateur ${user.id} mis à jour avec succès`);
          }
        } else {
          console.log(`L'utilisateur ${user.id} est déjà actif, aucune mise à jour nécessaire`);
        }
      } catch (error) {
        console.error(`Erreur lors de la vérification de l'utilisateur ${user.id}:`, error);
      }
    }
    
    console.log("Fin de la vérification et de la mise à jour des utilisateurs");
  };
  

const handleCheckAndUpdateUsers = async () => {
  let filtered = filterUsers(users, filters);

  if (startFromId) {
    const startIndex = filtered.findIndex((u) => u.id === parseInt(startFromId));
    if (startIndex !== -1) {
      filtered = filtered.slice(startIndex); // On ignore les utilisateurs avant l'ID spécifié
    }
  }

  if (reverseOrder) {
    filtered = [...filtered].reverse();
  }

  await checkAndUpdateUsers(filtered);

  const response = await fetch(apiPrefix + 'users', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des utilisateurs après mise à jour');
  }

  const updatedUsers = await response.json();
  setUsers(updatedUsers);
};


  const handleDeleteSelectedUsers = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer les utilisateurs sélectionnés ?')) {
      for (let id of selectedUsers) {
        try {
          const response = await fetch(apiPrefix + `users/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Erreur lors de la suppression de l'utilisateur avec l'ID ${id}`);
          }

          // Mise à jour de l'état pour supprimer les utilisateurs supprimés
          setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
        } catch (error) {
          console.error(`Erreur lors de la suppression de l'utilisateur ${id}:`, error);
        }
      }

      // Réinitialisation des utilisateurs sélectionnés après la suppression
      setSelectedUsers([]);
    }
  };

  const parseDate = (dateString) => {
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours, minutes, seconds] = timePart ? timePart.split(':').map(Number) : [0, 0, 0];
    return new Date(year, month - 1, day, hours, minutes, seconds);
  };

  const filterUsers = (users, filters) => {
    return users.filter(user => {
      return filters.every(filter => {
        const { column, value } = filter;
        const term = value.toLowerCase();
  
        if (column === 'all') {
          return Object.values(user).some(field => {
            if (field === null && term === 'null') return true; // Null explicite
            if (typeof field === 'string' && field.toLowerCase().includes(term)) return true;
            if (typeof field === 'number' && field.toString().includes(term)) return true;
            return false;
          });
        }

        if (column === 'loginCount') {
          const operatorMatch = value.match(/^([<>]=?|=)?\s*(\d+)$/); // Match les opérateurs et la valeur
          if (operatorMatch) {
            const [, operator, number] = operatorMatch;
            const numericValue = parseInt(number, 10);
  
            switch (operator) {
              case '>':
                return user.loginCount > numericValue;
              case '>=':
                return user.loginCount >= numericValue;
              case '<':
                return user.loginCount < numericValue;
              case '<=':
                return user.loginCount <= numericValue;
              case '=':
              case undefined: // Pas d'opérateur, correspondance exacte
                return user.loginCount === numericValue;
              default:
                return false;
            }
          } else {
            // Recherche exacte sans opérateurs si aucune correspondance n'est trouvée
            return user.loginCount === parseInt(term, 10);
          }
        }
  
        if (column === 'active') {
          const isActive = user.active ? 'oui' : 'non';
          return isActive.includes(term);
        }
  
        if (column === 'is_admin') {
          const isAdmin = user.is_admin ? 'oui' : 'non';
          return isAdmin.includes(term);
        }

        if (column === 'chefEntreprise') {
          const isChef = user.chefEntreprise ? 'oui' : 'non';
          return isChef.includes(term);
        }

        // Gestion spéciale pour le filtrage par SIRET avec plusieurs valeurs
        if (column === 'siret') {
          // Fonction pour normaliser un SIRET (supprimer espaces, tirets, points)
          const normalizeSiret = (siret) => {
            if (!siret) return '';
            return siret.toString().toLowerCase().replace(/[\s\-\.]/g, '');
          };

          // Séparer les SIRETs par virgule, point-virgule ou saut de ligne
          const siretList = value.split(/[,;\n]/).map(s => s.trim()).filter(s => s.length > 0);
          if (siretList.length > 1) {
            // Si plusieurs SIRETs sont fournis, vérifier si le SIRET de l'utilisateur correspond à l'un d'eux
            const userSiret = normalizeSiret(user.siret);
            return siretList.some(siret => {
              const cleanSiret = normalizeSiret(siret);
              return userSiret === cleanSiret || userSiret.includes(cleanSiret) || cleanSiret.includes(userSiret);
            });
          }
          // Si un seul SIRET, comportement normal avec normalisation
          const userSiret = normalizeSiret(user.siret);
          const searchSiret = normalizeSiret(value);
          if (userSiret === '' && searchSiret === '') return true;
          if (userSiret === '' || searchSiret === '') return false;
          return userSiret === searchSiret || userSiret.includes(searchSiret) || searchSiret.includes(userSiret);
        }
  
        if ((column === 'creationDate' || column === 'lastactive') && user[column]) {
          const userDate = new Date(user[column]);
          const searchDate = parseDate(term);
          return userDate.toLocaleDateString() === searchDate.toLocaleDateString();
        }
  
        const field = user[column];
        if (field === null && term === 'null') return true;
        if (typeof field === 'string' && field.toLowerCase().includes(term)) return true;
        if (typeof field === 'number' && field.toString().includes(term)) return true;
  
        return false;
      });
    });
  };
  

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(apiPrefix + 'users', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });


        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des utilisateurs');
        }

        const data = await response.json();
        console.log(data)
        if (!Array.isArray(data)) {
          console.error('La réponse de l\'API n\'est pas un tableau :', data);
          return;
        }

        setUsers(data);
      } catch (error) {
        console.error('Erreur:', error);
      }
    };

    fetchUsers();
  }, [token]);

  //const filteredUsers = search.trim() === '' ? users : filterUsers(users, search, column);
  const filteredUsers = filters.length === 0 ? users : filterUsers(users, filters);

  const exportToCsv = () => {
    const csv = Papa.unparse(filteredUsers);
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const csvURL = window.URL.createObjectURL(csvData);
    let tempLink = document.createElement('a');
    tempLink.href = csvURL;
    tempLink.setAttribute('download', 'filtered_users.csv');
    tempLink.click();
  };

  // Gestion de la pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container-responsive section-padding">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Tous les utilisateurs</h1>
      <ImportCsvExcel />

      <UserCount count={filteredUsers.length} />

      {/* Bouton pour vérifier et mettre à jour les utilisateurs */}
      <button 
        onClick={handleCheckAndUpdateUsers}
        className="btn-primary mb-4"
      >
        Vérifier et mettre à jour les utilisateurs
      </button>

<div className="flex items-center gap-4 my-4">
  <label htmlFor="startFromId" className="font-semibold">
    ID de départ :
  </label>
  <input
    id="startFromId"
    type="text"
    placeholder="ex: 120"
    value={startFromId}
    onChange={(e) => setStartFromId(e.target.value)}
    className="border border-gray-300 rounded px-2 py-1 w-24"
  />
  <label className="inline-flex items-center space-x-2">
    <input
      type="checkbox"
      checked={reverseOrder}
      onChange={() => setReverseOrder(!reverseOrder)}
      className="accent-blue-600"
    />
    <span>Commencer par la fin</span>
  </label>
</div>



      {/* Bouton pour exporter les utilisateurs en CSV */}
      <button 
        onClick={exportToCsv}
        className="btn-secondary mb-4 mr-4"
      >
        Exporter en CSV
      </button>

      {/* Bouton pour supprimer les utilisateurs sélectionnés */}
      <button 
        onClick={handleDeleteSelectedUsers} 
        disabled={selectedUsers.length === 0}
        className="btn-danger mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Supprimer les utilisateurs sélectionnés
      </button>

      <AdminCreateUser/>
      
      <Filters
  columns={columns}
  onChange={(newFilters) => setFilters(newFilters)} // Met à jour les filtres
/>



      <div className="card mt-6">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
              <input
              type="checkbox"
              checked={selectedUsers.length === currentUsers.length && currentUsers.length > 0}
              onChange={handleSelectAllUsers}
              className="w-5 h-5 accent-blue-600"
            />

                <th>ID</th>
                <th>Username</th>
                <th>Prénom</th>
                <th>Nom</th>
                <th>Actif</th>
                <th>Admin</th>
                <th>Siret</th>
                <th>Company</th>
                <th>Date de création</th>
                <th>Last Activity</th>
                <th>Connexions</th>
                <th>Chef d'entreprise</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map(user => (
                <tr key={user.id}>
<td className="text-center align-middle">
  <input
    type="checkbox"
    checked={selectedUsers.includes(user.id)}
    onChange={() => handleSelectUser(user.id)}
    className="w-5 h-5 accent-blue-600"
  />
</td>

                  <td><Link to={`/admin/users/${user.id}`}>{user.id}</Link></td>
                  <td><Link to={`/admin/users/${user.id}`}>{user.username}</Link></td>
                  <td><Link to={`/admin/users/${user.id}`}>{user.firstname}</Link></td>
                  <td>{user.lastname}</td>
                  <td>{user.active ? 'Oui' : 'Non'}</td>
                  <td>{user.is_admin ? 'Oui' : 'Non'}</td>
                  <td>{user.siret}</td>
                  <td>{user.company}</td>
                  <td>{user.creationDate ? new Date(user.creationDate).toLocaleString() : "Pas de date"}</td>
                  <td>{user.lastactive ? new Date(user.lastactive).toLocaleString() : "Pas d'activité"}</td>
                  <td>{user.loginCount}</td>
                  <td>{user.chefEntreprise ? 'Oui' : 'Non'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className='pagination'>
        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
          Précédent
        </button>
        <button onClick={() => paginate(currentPage + 1)} disabled={indexOfLastUser >= filteredUsers.length}>
          Suivant
        </button>
      </div>
    </div>
  );
};

export default AdminUsers;
